import { eventQBus } from "../types/EventQBus";
import NextBestAction from "../nextBestAction/NextBestAction.svelte";
import NextBestActionDesktop from "../nextBestAction/NextBestActionDesktop.svelte";
import FacetValueTags from "../facetvaluetags/FacetValueTags.svelte";
import FacetValueTagsWrapper from "../facetvaluetags/FacetValueTagsWrapper.svelte";
import Filterbar from "../nextBestAction/Filterbar.svelte";
import FilterBanner from "../filterBanner/FilterBanner.svelte";
import DesktopSorting from "../sorting/DesktopSorting.svelte";
import ConfigurableFacet from "../facet/ConfigurableFacet.svelte";
import FacetGroupDropdown from "../facetGroup/FacetGroupDropdown.svelte";
import FilterForm from "../filter/FilterForm.svelte";
import FilterAccordion from "../filter/FilterAccordion.svelte";
import FilterTitles from "../filter/FilterTitles.svelte";
import FilterList from "../filter/FilterList.svelte";
import type { FilterFormSerialized } from "../filter/FilterFormTypes";
import type { ConfigurableFilterOptions } from "../lib/filter";
import {
  Assets,
  setActiveConfiguration,
  setAllLevelSheet,
  setOpenFilterIds,
  toggleFilterAccordion,
} from "../lib/filter";
import { getActiveVariation, isActiveVariation } from "../experiment/Experiments";
import type { Component } from "svelte";
import { mount } from "svelte";
import type { Facet } from "../lib/facet";
import { setLoadNotShownValuesAsynchronously } from "../lib/facet";
import { clear, mapElements, notEmpty } from "../util/Utils";
import { isDesktop, isMobile } from "../util/Breakpoint";
import { CustomRangeInput } from "./slider/CustomRangeInput";
import { events } from "../events";
import FilterHarmonized from "../filter/FilterHarmonized.svelte";
import { FilterTitle } from "./FilterTitle";
import { isActive } from "../toggle/Toggle";
import { NextBestActionComponent, resetNBAScrollTracking } from "../nextBestAction/NextBestActionComponent";
import { refinementBar } from "../filtersheet/Selectors";
import { OcFilterAccordion } from "./OcFilterAccordion";
import { createLog } from "../util/log";

const log = createLog("mount");

/**
 *
 *
 *
 *
 */
/*                                                          */
function parseSerialized<T = any>(elem: HTMLElement, component: string, defaultJson?: string): T {
  const raw = elem.dataset.serialized ?? defaultJson;
  try {
    return JSON.parse(raw as string) as T;
  } catch (e) {
    throw new Error(
      `Invalid data-serialized for ${component} (filterId: ${elem.dataset.filterId}, payload starts with: ${String(raw).slice(0, 200)}): ${(e as Error).message}`,
      { cause: e },
    );
  }
}

/*                                                                                                            */
const config: Record<string, ConfigurableFilterOptions> = {
  color: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "100",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
    },
  },
  rating: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "rating",
              },
            },
          },
        },
      },
    },
  },
  singleSelectRange: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                fitContent: true,
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  intervalDeprecated: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "overflowBox",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  interval: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  switch: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  radio: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  chip: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  ottoLogo: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "textWithLogo",
                size: "100",
                logo: "otto",
              },
            },
          },
        },
      },
    },
  },
  deliveryFlatLogo: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "textWithLogo",
                size: "75",
                logo: "up",
                logoPosition: "left",
                showCount: true,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "textWithLogo",
                size: "75",
                logo: "up",
                logoPosition: "left",
                showCount: true,
              },
            },
          },
        },
      },
    },
  },
  default: {
    filterConfig: {
      default: {
        facetConfig: {
          facetValueGroupConfig: {
            facetValueConfig: {
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                showCount: false,
                size: "75",
              },
            },
          },
        },
      },
    },
  },
};

export { setOpenFilterIds, toggleFilterAccordion };

export function initializeConfigurableFacet(fragment: DocumentFragment) {
  /*                                                                                                       */
  setActiveConfiguration(isMobile() ? "sheetFirstLevel" : "default");
  mapElements(".ts_heureka_svelteFacet", (elem) => createFacet(elem, fragment), fragment)
    .filter(notEmpty)
    .map(CustomRangeInput.init);
}

function createFacet(elem: HTMLElement, fragment?: DocumentFragment) {
  const parentElement = elem.parentElement as HTMLElement;
  const { facetVisibility, filterTitleMode, hasNbaValues } = elem.dataset;
  const facet: Facet = parseSerialized<Facet>(elem, "ConfigurableFacet");
  if (isMobile() && facet.id.toLowerCase() === "retailer_otto") {
    /*                                                                 */
    log.debug("facet removed on mobile instead of mounted", { id: facet.id, filterId: facet.filterId });
    const retailerOttoFilterTitle = FilterTitle.filterId(facet.filterId, fragment);
    const retailerOttoFilterAccordion = OcFilterAccordion.filterId(facet.filterId, fragment);
    retailerOttoFilterTitle?.remove();
    retailerOttoFilterAccordion?.remove();
    return;
  }
  const filterConfig =
    (facet.id.toLowerCase() === "retailprice" && config["singleSelectRange"]) ||
    (facet.selectionType === "range" &&
      (isActive("HEUREKA_2320_NO_HORIZONTAL_SCROLLING_FOR_DIMENSION_FACETS")
        ? config["interval"]
        : config["intervalDeprecated"])) ||
    (isDesktop() && facet.id.toLowerCase() === "retailer_otto" && config["ottoLogo"]) ||
    (getActiveVariation("e3921") === "Logo" &&
      facet.id.toLowerCase() === "deliveryflat" &&
      config["deliveryFlatLogo"]) ||
    (facet.selectionType === "switch" && config["switch"]) ||
    (facet.selectionType === "radio" && config["radio"]) ||
    (facet.selectionType === "chip" && config["chip"]) ||
    (facet.selectionType === "rating" && config["rating"]) ||
    (facet.selectionType === "colorTile" && config["color"]) ||
    config["default"];

  mount(ConfigurableFacet, {
    target: parentElement,
    props: {
      data: {
        ...facet,
        showTitle: elem.dataset.showTitle === "true",
        useSlider: elem.dataset.mayHideSlider !== "true",
        parentId: `find_filter_${facet.filterId}`,
      },
      filterTitleMode: filterTitleMode,
      visibility: facetVisibility,
      hasNbaValues: hasNbaValues,
      ...filterConfig,
    },
  });
  elem.remove();
  return parentElement;
}

export function initializeNextBestActionComponent(
  componentType: Component<NextBestAction> | Component<NextBestActionDesktop>,
  refinementBar: HTMLElement | undefined,
  isDesktopNBA: boolean = false,
) {
  if (refinementBar) {
    if (isDesktopNBA) {
      /*                                                                                    */
      const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
      if (
        refinementBarPlaceholder &&
        !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
      ) {
        refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
      }
    }
    const initializationClass = isDesktopNBA
      ? "ts_heureka_svelteDesktopNextBestAction"
      : "ts_heureka_svelteNextBestAction";

    mapElements(`.${initializationClass}`, (elem) => {
      const parentElement = elem.parentElement as HTMLElement;

      /*                                                         */
      const parsedData = parseSerialized(elem, isDesktopNBA ? "NextBestActionDesktop" : "NextBestAction");

      /*                                                                                                             */
      /*                                                     */
      if (parsedData.values && isMobile()) {
        parsedData.values.forEach((value) => {
          if (value.name === "preis-in-eur") {
            value.referencedFacetValueId = "facetValue_retailprice_value";
          }
        });
      }
      mount(componentType, {
        target: refinementBar,
        props: {
          ...parsedData,
          containsOnlyNbaValues: elem.dataset.containsOnlyNbaValues === "true",
          withExpander: isDesktopNBA,
        },
      });
      elem.remove();
      return parentElement;
    });
  } else {
    log.debug("next best action not initialized: refinement bar not found", { isDesktopNBA });
  }
}

export function initializeFacetValueTags() {
  const initializationClass = "ts_heureka_svelteFacetValueTags";

  mapElements(`.${initializationClass}`, (elem) => {
    const parentElement = elem.parentElement as HTMLElement;

    /*                                                         */
    const parsedData = parseSerialized(elem, "FacetValueTags");
    mount(FacetValueTags, {
      target: parentElement,
      props: {
        ...parsedData,
      },
    });
    elem.remove();
    return parentElement;
  });

  /*                                                                                           */
  /*                                                        */
  mapElements(".ts_heureka_svelteFacetValueTagsWrapper", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;

    mount(FacetValueTagsWrapper, {
      target: parentElement,
      anchor: elem,
      props: parseSerialized(elem, "FacetValueTagsWrapper", "{}"),
    });
    elem.remove();
    return parentElement;
  });
}

export function initializeFilterbarComponent(refinementBar: HTMLElement | undefined) {
  if (refinementBar) {
    const initializationClass = "ts_heureka_svelteNextBestAction";

    mapElements(`.${initializationClass}`, (elem) => {
      const parentElement = elem.parentElement as HTMLElement;

      /*                                                         */
      const parsedData = parseSerialized(elem, "Filterbar");

      mount(Filterbar, {
        target: refinementBar,
        props: {
          ...parsedData,
        },
      });
      elem.remove();
      return parentElement;
    });
  } else {
    log.debug("filterbar not initialized: refinement bar not found");
  }
}

export function initializeQuickFilterHarmonizedComponent() {
  /*                              */
  if (document.getElementById("harmonized-filter-btn")) {
    log.debug("quick filter button not mounted: it is already present");
    return;
  }
  const cassandraPlaceholder = document.getElementById("cassandra-floating-btn");
  if (cassandraPlaceholder) {
    const switchPositionOnFloatingButton = isActiveVariation("e3972", "E3972B");
    log.debug("mounting quick filter button", { switchPositionOnFloatingButton });
    mount(FilterHarmonized, {
      target: cassandraPlaceholder, /*                                         */
      props: { hideAboveRefinementBar: true, switchPositionOnFloatingButton },
    });
  } else {
    log.debug("quick filter button not mounted: placeholder #cassandra-floating-btn not found");
  }
}

function initializeAssets() {
  mount(Assets, {
    target: document.body,
  });
}

export function initializeDesktopSortingComponent() {
  mapElements(".ts_heureka_svelteDesktopSorting", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;

    mount(DesktopSorting, {
      target: parentElement,
      props: {
        ...parseSerialized(elem, "DesktopSorting"),
      },
    });
    elem.remove();
    return parentElement;
  });
}

export function initializeFilterBannerComponent() {
  mapElements(".ts_heureka_svelteFilterBanner", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;

    mount(FilterBanner, {
      target: parentElement,
      props: {
        ...parseSerialized(elem, "FilterBanner"),
      },
    });
    elem.remove();
    return parentElement;
  });
}

/**
 *
 *
 *
 */
export function initializeFilterListComponent(fragment: ParentNode = document) {
  mapElements(
    ".ts_heureka_svelteFilterList",
    (elem) => {
      const parentElement = elem.parentElement as HTMLElement;

      mount(FilterList, {
        target: parentElement,
        anchor: elem,
        props: parseSerialized(elem, "FilterList", "{}"),
      });
      elem.remove();
      return parentElement;
    },
    fragment,
  );
}

/**
 *
 *
 *
 */
export function initializeFilterTitlesComponent(fragment: ParentNode = document) {
  mapElements(
    ".ts_heureka_svelteFilterTitles",
    (elem) => {
      const parentElement = elem.parentElement as HTMLElement;

      mount(FilterTitles, {
        target: parentElement,
        anchor: elem,
        props: parseSerialized(elem, "FilterTitles", "{}"),
      });
      elem.remove();
      return parentElement;
    },
    fragment,
  );
}

/**
 *
 *
 *
 *
 *
 */
export function initializeFilterFormComponents(fragment: ParentNode = document) {
  const renderAccordionShell = isActive("HEUREKA_2167_MIGRATE_FILTERS_TO_SVELTE");
  mapElements(
    ".ts_heureka_svelteFilterForm",
    (elem) => {
      const parentElement = elem.parentElement as HTMLElement;
      const { position } = elem.dataset;
      const { accordion, ...form } = parseSerialized<FilterFormSerialized>(elem, "FilterForm", "{}");
      const formProps = { ...form, position: position ? Number(position) : 0 };

      /*                                                                                  */
      /*                                                                                           */
      if (renderAccordionShell && accordion) {
        mount(FilterAccordion, {
          target: parentElement,
          anchor: elem,
          props: { accordion, form: formProps },
        });
      } else {
        mount(FilterForm, {
          target: parentElement,
          anchor: elem,
          props: formProps,
        });
      }
      elem.remove();
      return parentElement;
    },
    fragment,
  );
}

function initializeFacetGroups() {
  mapElements(".ts_heureka_svelteFacetGroupDropdown", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;

    mount(FacetGroupDropdown, {
      target: parentElement,
      props: {
        ...parseSerialized(elem, "FacetGroupDropdown"),
        label: elem.dataset.label,
        name: elem.dataset.name,
        filterId: elem.dataset.filterId,
      },
    });
    elem.remove();
    return parentElement;
  });
}

export default function registerSvelteComponentInitialization() {
  log.debug("registering svelte component initialization", {
    mobile: isMobile(),
    /*                                                                                  */
    e3911: getActiveVariation("e3911", "StatusQuo"),
    filtersToSvelte: isActive("HEUREKA_2167_MIGRATE_FILTERS_TO_SVELTE"),
    filterBannerToSvelte: isActive("HEUREKA_2167_MIGRATE_FILTER_BANNER_TO_SVELTE"),
    desktopSortingToSvelte: isActive("HEUREKA_2167_MIGRATE_DESKTOP_SORTING_TO_SVELTE"),
  });
  /*                                                                                                */
  setLoadNotShownValuesAsynchronously(isActive("HEUREKA_2398_LOAD_NOT_SHOWN_VALUES_ASYNCHRONOUSLY"));
  /*                                                                                                        */
  /*                                                                                                       */
  setAllLevelSheet(isActive("HEUREKA_2398_ALL_LEVEL_SHEET") || isActive("HEUREKA_2398_HC_SHEET"));
  if (isActive("HEUREKA_2167_MIGRATE_FILTER_BANNER_TO_SVELTE")) {
    events.onFilterSectionLoaded(initializeFilterBannerComponent);
  }
  if (isActive("HEUREKA_2167_MIGRATE_DESKTOP_SORTING_TO_SVELTE")) {
    events.onFilterSectionLoaded(initializeDesktopSortingComponent);
  }
  events.onFilterSectionLoaded(initializeFacetGroups);
  if (isMobile()) {
    events.onFilterSectionLoaded(() => {
      const refBar = refinementBar();
      clear(refBar);
      if (NextBestActionComponent.couldBeVisible()) {
        resetNBAScrollTracking();
      }
      if (getActiveVariation("e3911", "StatusQuo") !== "StatusQuo") {
        initializeFacetValueTags();
        initializeFilterbarComponent(refBar);
      } else {
        initializeNextBestActionComponent(NextBestAction, refBar);
      }
    });
    events.onFilterSectionLoaded(initializeQuickFilterHarmonizedComponent);
  } else {
    events.onFilterSectionLoaded(() => {
      const refBar = refinementBar();
      clear(refBar);
      initializeNextBestActionComponent(NextBestActionDesktop, refBar, true);
    });
  }
  eventQBus.once("heureka.filterSection.loaded", initializeAssets);
}
